import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { ScoreRing } from '../components/SharedUI';
import { API_BASE } from '../../config';

const AGENT_DISPLAY: Record<string, string> = {
  coo_scoring: 'COO Scoring Agent',
  qrs: 'Quality Rating System (QRS)',
  pns: 'Political Nexus Scoring (PNS)',
};

// ============================================================
// TYPES
// ============================================================

interface Finding {
  type: string;
  category: string;
  suggestion?: string;
  impact?: string;
  source?: string;
  factor?: string;
  result?: string;
  potential_weight_gain?: number;
  current_weight_pct?: number;
  suggested_weight_pct?: number;
  weight_delta?: number;
  suggested_direction?: string;
  potential_factors?: string[];
  existing_factors?: string[];
  priority?: string;
  sources_affected?: string[];
  details?: string;
  ai_generated?: boolean;
  finding_id?: number;
  resolved?: boolean;
  scoring_agent_tier?: string;
  scoring_agent_score?: number;
  reconciler_tier?: string;
  reconciler_reason?: string;
  scoring_agent_coo?: string;
  reconciler_coo?: string;
  llm_coo?: string;
  llm_tier?: string;
  llm_reason?: string;
  llm_disputes_coo?: boolean;
  [key: string]: unknown;
}

interface ProductFinding {
  id: number;
  ndc: string;
  product_name: string;
  manufacturer: string;
  coo_country: string;
  coo_score: number;
  quality_tier: string;
  reconciler_tier: string;
  reconciler_override: boolean;
  reconciler_conflict: boolean;
  findings: Finding[];
  summary: string;
  new_sources_count: number;
  new_factors_count: number;
  data_discrepancies_count: number;
  missed_factors_count: number;
  rescrape_flags_count: number;
  logic_adjustments_count: number;
  weight_adjustments_count: number;
  confidence_overrides_count: number;
  reconciler_conflicts_count: number;
  potential_score_improvement: number;
  analyzed_at: string;
}

interface SummaryData {
  total_products_analyzed: number;
  avg_score: number;
  avg_potential_improvement: number;
  total_new_sources_suggested: number;
  total_new_factors_suggested: number;
  total_data_discrepancies: number;
  total_missed_factors: number;
  total_rescrape_flags: number;
  total_logic_adjustments: number;
  total_weight_adjustments: number;
  total_confidence_overrides: number;
  total_reconciler_conflicts: number;
  first_analyzed: string | null;
  last_analyzed: string | null;
}

interface ApiFindingRow {
  id: number;
  ndc_code: string;
  agent_id: string;
  finding_type: string;
  severity: string;
  title: string;
  description: string;
  details: Record<string, unknown>;
  resolved: boolean;
  resolved_at: string | null;
  scoring_cycle_id: string;
  created_at: string;
  product_name?: string | null;
  manufacturer?: string | null;
  coo_country?: string | null;
  coo_score?: number | null;
  quality_tier?: string | null;
  reconciler_tier?: string | null;
  reconciler_override?: boolean | null;
  reconciler_conflict?: boolean | null;
  reconciler_reason?: string | null;
  di_summary?: string | null;
}

interface ApiSummary {
  agent_id: string;
  total: number;
  resolved: number;
  unresolved: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  first_analyzed?: string | null;
  last_analyzed?: string | null;
}

interface ImprovementHistoryEntry {
  cycle_id: string;
  date: string;
  high_tier_products: number;
  findings_resolved: number;
  override_resolution_rate: number;
}

interface ImprovementHistoryData {
  entries: ImprovementHistoryEntry[];
  summary: {
    high_tier_products: number;
    findings_resolved: number;
    override_resolution_rate: number;
  };
}

interface ScoringCycle {
  cycle_id: string;
  date: string;
}

type PageView = 'details' | 'products';
type DetailsTab = 'overview' | 'findings' | 'history';

// ============================================================
// CONSTANTS
// ============================================================

const FINDING_TYPES: Array<{
  key: string;
  label: string;
  color: string;
  description: string;
}> = [
  { key: 'confidence_override', label: 'Confidence Overrides', color: '#e11d48', description: 'Reconciler assessed a different tier than the scoring agent' },
  { key: 'reconciler_conflict', label: 'Reconciler Conflicts', color: '#d97706', description: 'LLM queue disputes the Reconciler\'s COO conclusion' },
  { key: 'new_source', label: 'New Sources', color: '#8B5CF6', description: 'Additional data sources suggested to improve scoring accuracy' },
  { key: 'new_factor', label: 'New Factors', color: '#3B82F6', description: 'New scoring factors suggested for existing data sources' },
  { key: 'data_discrepancy', label: 'Discrepancies', color: '#EF4444', description: 'Contradictions or inconsistencies found in evidence data' },
  { key: 'missed_factor', label: 'Missed Factors', color: '#F97316', description: 'Scoring factors that missed due to missing or stale data' },
  { key: 'rescrape_flag', label: 'Rescrape Flags', color: '#F59E0B', description: 'Product should be re-scraped for updated evidence' },
  { key: 'logic_adjustment', label: 'Logic Adjustments', color: '#06B6D4', description: 'Suggested improvements to scoring logic or factor evaluation' },
  { key: 'weight_adjustment', label: 'Weight Adjustments', color: '#10B981', description: 'Suggested changes to factor weighting for more accurate scores' },
];

// ============================================================
// HELPERS
// ============================================================

function formatTimestamp(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-US');
}

function formatDateShort(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function tierColor(tier: string): string {
  switch (tier) {
    case 'HIGH': return '#22C55E';
    case 'MODERATE': case 'MEDIUM': return '#F59E0B';
    case 'LOW': return '#EF4444';
    default: return '#94A3B8';
  }
}

function findingTypeColor(t: string): string {
  const found = FINDING_TYPES.find(ft => ft.key === t);
  return found ? found.color : '#94A3B8';
}

function findingTypeLabel(t: string): string {
  const found = FINDING_TYPES.find(ft => ft.key === t);
  return found ? found.label : t.replace(/_/g, ' ');
}

function getCountForType(pf: ProductFinding, type: string): number {
  switch (type) {
    case 'confidence_override': return pf.confidence_overrides_count || 0;
    case 'reconciler_conflict': return pf.reconciler_conflicts_count || 0;
    case 'new_source': return pf.new_sources_count || 0;
    case 'new_factor': return pf.new_factors_count || 0;
    case 'data_discrepancy': return pf.data_discrepancies_count || 0;
    case 'missed_factor': return pf.missed_factors_count || 0;
    case 'rescrape_flag': return pf.rescrape_flags_count || 0;
    case 'logic_adjustment': return pf.logic_adjustments_count || 0;
    case 'weight_adjustment': return pf.weight_adjustments_count || 0;
    default: return 0;
  }
}

function getTotalCount(summary: SummaryData | null, type: string): number {
  if (!summary) return 0;
  switch (type) {
    case 'confidence_override': return summary.total_confidence_overrides || 0;
    case 'reconciler_conflict': return summary.total_reconciler_conflicts || 0;
    case 'new_source': return summary.total_new_sources_suggested || 0;
    case 'new_factor': return summary.total_new_factors_suggested || 0;
    case 'data_discrepancy': return summary.total_data_discrepancies || 0;
    case 'missed_factor': return summary.total_missed_factors || 0;
    case 'rescrape_flag': return summary.total_rescrape_flags || 0;
    case 'logic_adjustment': return summary.total_logic_adjustments || 0;
    case 'weight_adjustment': return summary.total_weight_adjustments || 0;
    default: return 0;
  }
}

function transformApiSummary(raw: ApiSummary, productCount: number): SummaryData {
  const bt = raw.by_type || {};
  return {
    total_products_analyzed: productCount,
    avg_score: 0,
    avg_potential_improvement: 0,
    total_new_sources_suggested: bt.new_source || 0,
    total_new_factors_suggested: bt.new_factor || 0,
    total_data_discrepancies: bt.data_discrepancy || 0,
    total_missed_factors: bt.missed_factor || 0,
    total_rescrape_flags: bt.rescrape_flag || 0,
    total_logic_adjustments: bt.logic_adjustment || 0,
    total_weight_adjustments: bt.weight_adjustment || 0,
    total_confidence_overrides: bt.confidence_override || 0,
    total_reconciler_conflicts: bt.reconciler_conflict || 0,
    first_analyzed: raw.first_analyzed || null,
    last_analyzed: raw.last_analyzed || null,
  };
}

function groupFindingsByProduct(rows: ApiFindingRow[]): ProductFinding[] {
  const byNdc = new Map<string, ApiFindingRow[]>();
  for (const row of rows) {
    const ndc = row.ndc_code || 'unknown';
    if (!byNdc.has(ndc)) byNdc.set(ndc, []);
    byNdc.get(ndc)!.push(row);
  }
  const result: ProductFinding[] = [];
  let idx = 0;
  for (const [ndc, ndcRows] of Array.from(byNdc.entries())) {
    const findings: Finding[] = ndcRows.map(r => {
      const d = r.details || {};
      return {
        finding_id: r.id,
        resolved: r.resolved || false,
        type: r.finding_type || (d.type as string) || '',
        category: (d.category as string) || '',
        suggestion: (d.suggestion as string) || r.description || '',
        impact: (d.impact as string) || undefined,
        source: (d.source as string) || undefined,
        factor: (d.factor as string) || undefined,
        result: (d.result as string) || undefined,
        potential_weight_gain: d.potential_weight_gain as number | undefined,
        current_weight_pct: d.current_weight_pct as number | undefined,
        suggested_weight_pct: d.suggested_weight_pct as number | undefined,
        weight_delta: d.weight_delta as number | undefined,
        suggested_direction: d.suggested_direction as string | undefined,
        potential_factors: d.potential_factors as string[] | undefined,
        existing_factors: d.existing_factors as string[] | undefined,
        priority: (d.priority as string) || r.severity || undefined,
        sources_affected: d.sources_affected as string[] | undefined,
        details: r.description || undefined,
        ai_generated: (d.ai_generated as boolean) || undefined,
        scoring_agent_tier: d.scoring_agent_tier as string | undefined,
        scoring_agent_score: d.scoring_agent_score as number | undefined,
        reconciler_tier: (d.reconciler_tier as string) || r.reconciler_tier || undefined,
        reconciler_reason: (d.reconciler_reason as string) || r.reconciler_reason || undefined,
        scoring_agent_coo: d.scoring_agent_coo as string | undefined,
        reconciler_coo: d.reconciler_coo as string | undefined,
        llm_coo: d.llm_coo as string | undefined,
        llm_tier: d.llm_tier as string | undefined,
        llm_reason: d.llm_reason as string | undefined,
        llm_disputes_coo: d.llm_disputes_coo as boolean | undefined,
      };
    });
    const countType = (t: string) => findings.filter(f => f.type === t).length;
    const dates = ndcRows.map(r => r.created_at).filter(Boolean).sort();
    const firstRow = ndcRows[0];
    result.push({
      id: idx++,
      ndc,
      product_name: firstRow.product_name || '',
      manufacturer: firstRow.manufacturer || '',
      coo_country: firstRow.coo_country || '',
      coo_score: firstRow.coo_score || 0,
      quality_tier: firstRow.quality_tier || '',
      reconciler_tier: firstRow.reconciler_tier || '',
      reconciler_override: firstRow.reconciler_override || false,
      reconciler_conflict: firstRow.reconciler_conflict || false,
      findings,
      summary: firstRow.di_summary || '',
      new_sources_count: countType('new_source'),
      new_factors_count: countType('new_factor'),
      data_discrepancies_count: countType('data_discrepancy'),
      missed_factors_count: countType('missed_factor'),
      rescrape_flags_count: countType('rescrape_flag'),
      logic_adjustments_count: countType('logic_adjustment'),
      weight_adjustments_count: countType('weight_adjustment'),
      confidence_overrides_count: countType('confidence_override'),
      reconciler_conflicts_count: countType('reconciler_conflict'),
      potential_score_improvement: 0,
      analyzed_at: dates.length > 0 ? dates[dates.length - 1] : '',
    });
  }
  return result;
}

// ============================================================
// COMPONENT
// ============================================================

export default function DataAnalyst() {
  const navigate = useNavigate();
  const { agentName } = useParams<{ agentName: string }>();
  const { T } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const agentId = agentName || 'coo_scoring';
  const agentDisplay = AGENT_DISPLAY[agentId] || agentId.replace(/_/g, ' ');
  const isQrs = agentId === 'qrs';

  // View state
  const [pageView, setPageView] = useState<PageView>('details');
  const [activeTab, setActiveTab] = useState<DetailsTab>('overview');
  const [selectedProductNdc, setSelectedProductNdc] = useState<string | null>(null);

  // Scoring cycle / run selector
  const [scoringCycles, setScoringCycles] = useState<ScoringCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');

  // Data state
  const [findings, setFindings] = useState<ProductFinding[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [historyData, setHistoryData] = useState<ImprovementHistoryData | null>(null);
  const [historyRange, setHistoryRange] = useState<'10' | '30' | 'all'>('10');

  // Products view state
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('all');

  // Pagination
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFindings, setTotalFindings] = useState(0);

  // Fetch scoring cycles
  const [cyclesLoaded, setCyclesLoaded] = useState(false);
  const fetchCycles = useCallback(async () => {
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/data-analyst/scoring-cycles?agent_id=${agentId}`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data: ScoringCycle[] = await resp.json();
        setScoringCycles(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0 && !selectedCycleId) {
          setSelectedCycleId(data[0].cycle_id);
        }
      }
    } catch {
      // cycles fetch is best-effort
    } finally {
      setCyclesLoaded(true);
    }
  }, [agentId, selectedCycleId]);

  // Derive cycles from findings as additional fallback
  const deriveCyclesFromFindings = useCallback((items: ApiFindingRow[]) => {
    if (scoringCycles.length > 0) return; // already have cycles from endpoint
    const map: Record<string, string> = {};
    for (const row of items) {
      const cid = row.scoring_cycle_id;
      if (cid && !map[cid]) {
        map[cid] = row.created_at || '';
      }
    }
    const derived = Object.entries(map)
      .map(([cycle_id, date]) => ({ cycle_id, date }))
      .sort((a, b) => (b.date > a.date ? 1 : -1));
    if (derived.length > 0) {
      setScoringCycles(derived);
      if (!selectedCycleId) {
        setSelectedCycleId(derived[0].cycle_id);
      }
    }
  }, [scoringCycles.length, selectedCycleId]);

  // Fetch findings data
  const fetchData = useCallback(async (findingType?: string, page: number = 1, cycleId?: string) => {
    try {
      const offset = (page - 1) * PAGE_SIZE;
      let findingsUrl = `${API_BASE}/api/metasupply/swarm/data-analyst/findings?agent_id=${agentId}&limit=${PAGE_SIZE}&offset=${offset}`;
      if (findingType && findingType !== 'all') {
        findingsUrl += `&finding_type=${encodeURIComponent(findingType)}`;
      }
      if (cycleId) {
        findingsUrl += `&scoring_cycle_id=${encodeURIComponent(cycleId)}`;
      }
      const [findingsResp, summaryResp] = await Promise.all([
        fetch(findingsUrl, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/data-analyst/summary?agent_id=${agentId}${cycleId ? `&scoring_cycle_id=${encodeURIComponent(cycleId)}` : ''}`, { credentials: 'include' }),
      ]);

      let hasError = false;
      let parsedFindings: ProductFinding[] = [];

      if (findingsResp.ok) {
        const data = await findingsResp.json();
        const items: ApiFindingRow[] = Array.isArray(data) ? data : (data.items || []);
        setTotalFindings(typeof data.total === 'number' ? data.total : items.length);
        parsedFindings = groupFindingsByProduct(items);
        setFindings(parsedFindings);
        // Derive cycles from findings if the dedicated endpoint returned nothing
        deriveCyclesFromFindings(items);
      } else {
        setError('Failed to fetch analysis findings');
        hasError = true;
      }

      if (summaryResp.ok) {
        const rawSummary: ApiSummary = await summaryResp.json();
        setSummary(transformApiSummary(rawSummary, parsedFindings.length));
      }

      if (!hasError) {
        setError(null);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [agentId, deriveCyclesFromFindings]);

  // Fetch improvement history
  const fetchHistory = useCallback(async () => {
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/data-analyst/improvement-history?range=${historyRange}&agent_id=${agentId}`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data = await resp.json();
        setHistoryData(data);
      }
    } catch {
      // silently ignore
    }
  }, [historyRange, agentId]);

  // Initial load
  useEffect(() => {
    fetchCycles();
  }, [fetchCycles]);

  // Only fetch data once we have a selected cycle — avoids the initial
  // unscoped request that returns ALL findings (slow) and shows zeros.
  // If cycles loaded but none exist, stop loading to show empty state.
  useEffect(() => {
    if (!selectedCycleId) {
      if (cyclesLoaded) setLoading(false);
      return;
    }
    fetchData(filterCategory, currentPage, selectedCycleId);
  }, [fetchData, filterCategory, currentPage, selectedCycleId, cyclesLoaded]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Resolve override handler
  const handleResolveOverride = useCallback(async (findingId: number, action: string) => {
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/data-analyst/resolve-override`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ finding_id: findingId, action }),
        },
      );
      if (resp.ok) {
        fetchData(filterCategory, currentPage, selectedCycleId || undefined);
      }
    } catch {
      // silently ignore
    }
  }, [fetchData, filterCategory, currentPage, selectedCycleId]);

  // Row expand toggle
  const toggleRow = useCallback((key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Derived data
  const filteredFindings = useMemo(() => findings, [findings]);

  const totalFindingsForProduct = useCallback((pf: ProductFinding) => {
    if (filterCategory === 'all') return pf.findings.length;
    return pf.findings.filter(f => f.type === filterCategory).length;
  }, [filterCategory]);

  const productsForGrid = useMemo(() => {
    let list = [...findings];
    if (tierFilter !== 'all') {
      list = list.filter(pf => pf.quality_tier === tierFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(pf =>
        pf.product_name.toLowerCase().includes(q) ||
        pf.ndc.toLowerCase().includes(q) ||
        pf.manufacturer.toLowerCase().includes(q) ||
        pf.coo_country.toLowerCase().includes(q)
      );
    }
    return list;
  }, [findings, tierFilter, searchQuery]);

  const selectedProduct = useMemo(() => {
    if (!selectedProductNdc) return null;
    return findings.find(pf => pf.ndc === selectedProductNdc) || null;
  }, [findings, selectedProductNdc]);

  const highScoringCount = useMemo(() => {
    return findings.filter(pf => pf.quality_tier === 'HIGH').length;
  }, [findings]);

  const handleCycleChange = useCallback((cycleId: string) => {
    setSelectedCycleId(cycleId);
    setCurrentPage(1);
    setExpandedRows(new Set());
  }, []);

  // Spinner
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 16 }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 36, height: 36, border: `3px solid ${T.border}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ fontSize: 14, color: T.textLight, fontWeight: 600 }}>Loading analysis data...</span>
      </div>
    );
  }

  // Product Detail View
  if (selectedProductNdc && selectedProduct) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <button
          onClick={() => setSelectedProductNdc(null)}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Products
        </button>

        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#FFFFFF', margin: '0 0 8px 0' }}>
              {selectedProduct.product_name || selectedProduct.ndc}
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
              <span>NDC: {selectedProduct.ndc}</span>
              <span>Manufacturer: {selectedProduct.manufacturer || '--'}</span>
              {!isQrs && <span>COO: {selectedProduct.coo_country || '--'}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <ScoreRing score={selectedProduct.coo_score} size={72} strokeWidth={6} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                padding: '2px 10px', borderRadius: 10, textAlign: 'center',
                background: `${tierColor(selectedProduct.quality_tier)}20`,
                color: tierColor(selectedProduct.quality_tier),
              }}>
                {selectedProduct.quality_tier || '--'}
              </span>
              {selectedProduct.reconciler_override && (
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                  background: '#e11d4820', color: '#e11d48', textAlign: 'center',
                }}>
                  OVERRIDE
                </span>
              )}
            </div>
          </div>
        </div>

        {selectedProduct.summary && (
          <div style={{
            background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
            padding: '16px 20px', fontSize: 13, color: T.textDark, lineHeight: 1.6,
            borderLeft: `4px solid ${T.gold}`,
          }}>
            <span style={{ fontWeight: 700, color: T.navy }}>Summary: </span>
            {selectedProduct.summary}
          </div>
        )}

        <div style={{
          background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
          boxShadow: T.shadow, padding: isMobile ? '20px 16px' : '24px 28px',
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: T.textDark, margin: '0 0 16px 0' }}>
            Findings ({selectedProduct.findings.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {selectedProduct.findings.map((finding, fIdx) => {
              const fType = finding.type || '';
              if (fType === 'confidence_override') {
                return <ConfidenceOverrideCard key={fIdx} finding={finding} T={T} productQualityTier={selectedProduct.quality_tier} productReconcilerTier={selectedProduct.reconciler_tier} />;
              }
              if (fType === 'reconciler_conflict') {
                return <ReconcilerConflictCard key={fIdx} finding={finding} T={T} onResolve={handleResolveOverride} productQualityTier={selectedProduct.quality_tier} productReconcilerTier={selectedProduct.reconciler_tier} />;
              }
              return <DefaultFindingCard key={fIdx} finding={finding} T={T} />;
            })}
            {selectedProduct.findings.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
                No findings for this product.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main page render
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/agent-activity')}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Agent Activity
        </button>

        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>
              Data Analyst &mdash; {agentDisplay}
            </h1>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', maxWidth: 600, lineHeight: 1.6, margin: '8px 0 0 0' }}>
              Aggregated intelligence from the scoring agent, reconciler, and LLM queue.
            </p>
          </div>

          {/* Details / Products Toggle */}
          <div style={{
            display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: 3,
          }}>
            {(['details', 'products'] as PageView[]).map(v => (
              <button
                key={v}
                onClick={() => { setPageView(v); setSelectedProductNdc(null); }}
                style={{
                  padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  background: pageView === v ? '#FFFFFF' : 'transparent',
                  color: pageView === v ? T.navy : 'rgba(255,255,255,0.6)',
                }}
              >
                {v === 'details' ? 'Details' : 'Products'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: T.dangerBg, border: `1px solid ${T.danger}30`,
          color: T.danger, fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* DETAILS VIEW */}
      {pageView === 'details' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Tab Bar */}
          <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${T.border}` }}>
            {([
              { key: 'overview' as DetailsTab, label: 'Overview' },
              { key: 'findings' as DetailsTab, label: 'Findings' },
              { key: 'history' as DetailsTab, label: 'History' },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '12px 24px', fontSize: 14, fontWeight: 700,
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: 'transparent', transition: 'all 0.15s',
                  color: activeTab === tab.key ? T.navy : T.textLight,
                  borderBottom: activeTab === tab.key ? `3px solid ${T.gold}` : '3px solid transparent',
                  marginBottom: -2,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Run Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>Scoring Run:</label>
                <select
                  value={selectedCycleId}
                  onChange={e => handleCycleChange(e.target.value)}
                  style={{
                    padding: '8px 32px 8px 12px', borderRadius: 8, fontSize: 13,
                    border: `1px solid ${T.border}`, background: T.white, color: T.textDark,
                    fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer',
                    appearance: 'none' as const,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                    minWidth: 220,
                  }}
                >
                  {scoringCycles.length === 0 && <option value="">No runs available</option>}
                  {scoringCycles.map(c => (
                    <option key={c.cycle_id} value={c.cycle_id}>
                      {formatDateShort(c.date)} — Run {c.cycle_id.slice(0, 8)}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 12, color: T.textLight }}>
                  {scoringCycles.length} run{scoringCycles.length !== 1 ? 's' : ''} available
                </span>
              </div>

              {/* Action Required Card */}
              <div style={{
                background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
                boxShadow: T.shadow, padding: isMobile ? '20px 16px' : '24px 28px',
                borderLeft: '4px solid #e11d48',
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: T.textDark, margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e11d48" strokeWidth="2">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Action Required
                </h3>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: '#e11d48' }}>
                      {summary?.total_confidence_overrides ?? 0}
                    </span>
                    <span style={{ fontSize: 13, color: T.textMid, fontWeight: 600 }}>Confidence Overrides</span>
                  </div>
                  <div style={{ width: 1, background: T.border, alignSelf: 'stretch' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: '#d97706' }}>
                      {summary?.total_reconciler_conflicts ?? 0}
                    </span>
                    <span style={{ fontSize: 13, color: T.textMid, fontWeight: 600 }}>Reconciler Conflicts</span>
                  </div>
                </div>
              </div>

              {/* Improvement Summary */}
              <div style={{
                background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
                boxShadow: T.shadow, padding: isMobile ? '20px 16px' : '24px 28px',
                borderLeft: `4px solid ${T.gold}`,
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: T.textDark, margin: '0 0 16px 0' }}>
                  Improvement Summary
                </h3>
                <div style={{ fontSize: 14, color: T.textDark, lineHeight: 1.8 }}>
                  <p style={{ margin: '0 0 12px 0' }}>
                    This scoring run analyzed <strong>{fmtNum(summary?.total_products_analyzed ?? findings.length)} products</strong>.
                    {highScoringCount > 0 && (
                      <> Of those, <strong>{fmtNum(highScoringCount)}</strong> achieved a <strong style={{ color: '#22C55E' }}>HIGH</strong> quality tier.</>
                    )}
                    {summary && summary.total_missed_factors > 0 && (
                      <> The agent identified <strong>{fmtNum(summary.total_missed_factors)} missed factors</strong> that could improve scoring accuracy if addressed.</>
                    )}
                  </p>
                  {summary && (summary.total_confidence_overrides > 0 || summary.total_reconciler_conflicts > 0) && (
                    <p style={{ margin: '0 0 12px 0' }}>
                      There {summary.total_confidence_overrides === 1 ? 'is' : 'are'} <strong style={{ color: '#e11d48' }}>{fmtNum(summary.total_confidence_overrides)} confidence override{summary.total_confidence_overrides !== 1 ? 's' : ''}</strong> where
                      the reconciler assessed a different tier than the scoring agent, requiring human review.
                      {summary.total_reconciler_conflicts > 0 && (
                        <> Additionally, <strong style={{ color: '#d97706' }}>{fmtNum(summary.total_reconciler_conflicts)} reconciler conflict{summary.total_reconciler_conflicts !== 1 ? 's' : ''}</strong> were
                        flagged where the LLM disputes the reconciler&apos;s COO conclusion.</>
                      )}
                    </p>
                  )}
                  {summary && (summary.total_new_sources_suggested > 0 || summary.total_new_factors_suggested > 0) && (
                    <p style={{ margin: '0 0 12px 0' }}>
                      The data analyst recommends adding <strong>{fmtNum(summary.total_new_sources_suggested)} new data source{summary.total_new_sources_suggested !== 1 ? 's' : ''}</strong> and <strong>{fmtNum(summary.total_new_factors_suggested)} new scoring factor{summary.total_new_factors_suggested !== 1 ? 's' : ''}</strong> to
                      strengthen evidence coverage across the portfolio.
                    </p>
                  )}
                  {summary && summary.total_weight_adjustments > 0 && (
                    <p style={{ margin: 0 }}>
                      Additionally, <strong>{fmtNum(summary.total_weight_adjustments)} weight adjustment{summary.total_weight_adjustments !== 1 ? 's' : ''}</strong> have been suggested to better align factor
                      weights with observed evidence quality.
                    </p>
                  )}
                </div>
                <div style={{ marginTop: 16, fontSize: 11, color: T.textLight, fontStyle: 'italic' }}>
                  This summary is generated by the scoring agent from its own evidence and data analysis.
                </div>
              </div>
            </div>
          )}

          {/* FINDINGS TAB */}
          {activeTab === 'findings' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Filter Pills */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setFilterCategory('all'); setCurrentPage(1); }}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    border: `1px solid ${filterCategory === 'all' ? T.navy : T.border}`,
                    background: filterCategory === 'all' ? `${T.navy}12` : T.white,
                    color: filterCategory === 'all' ? T.navy : T.textMid,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                >
                  All
                </button>
                {FINDING_TYPES.map(ft => {
                  const count = getTotalCount(summary, ft.key);
                  const active = filterCategory === ft.key;
                  return (
                    <button
                      key={ft.key}
                      onClick={() => { setFilterCategory(ft.key); setCurrentPage(1); }}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${active ? ft.color : T.border}`,
                        background: active ? `${ft.color}12` : T.white,
                        color: active ? ft.color : T.textMid,
                        cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {ft.label}
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                        background: active ? `${ft.color}20` : `${T.navy}08`,
                        color: active ? ft.color : T.textLight,
                      }}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Results Table */}
              <div style={{
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
              }}>
                {filteredFindings.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
                      No Analysis Findings Yet
                    </div>
                    <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
                      The Data Analyst aggregates findings from the scoring agent, reconciler, and LLM queue.
                    </div>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                          {(['NDC', 'Product', ...(isQrs ? [] : ['COO']), 'Score', 'Tier', 'Findings', 'Analyzed'] as string[]).map(col => (
                            <th key={col} style={{
                              padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                              color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                              whiteSpace: 'nowrap',
                            }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFindings.map((pf, idx) => {
                          const rowKey = `${pf.ndc}-${idx}`;
                          const isExpanded = expandedRows.has(rowKey);
                          const displayFindings = filterCategory === 'all'
                            ? pf.findings
                            : pf.findings.filter(f => f.type === filterCategory);
                          const totalCount = totalFindingsForProduct(pf);

                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                onClick={() => toggleRow(rowKey)}
                                style={{
                                  borderBottom: `1px solid ${T.borderLight}`,
                                  cursor: 'pointer',
                                  background: isExpanded ? `${T.gold}08` : 'transparent',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = `${T.navy}04`; }}
                                onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                              >
                                <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <svg
                                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                                      stroke={T.textLight} strokeWidth="2"
                                      style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
                                    >
                                      <path d="M6 9l6 6 6-6" />
                                    </svg>
                                    {pf.ndc}
                                  </div>
                                </td>
                                <td style={{ padding: '12px 16px', color: T.textDark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {pf.product_name || '--'}
                                </td>
                                {!isQrs && (
                                  <td style={{ padding: '12px 16px', color: T.textDark }}>{pf.coo_country || '--'}</td>
                                )}
                                <td style={{ padding: '12px 16px' }}>
                                  <span style={{ fontSize: 14, fontWeight: 700, color: tierColor(pf.quality_tier) }}>{pf.coo_score}%</span>
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{
                                      fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                      letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                                      background: `${tierColor(pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier)}18`,
                                      color: tierColor(pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier),
                                    }}>
                                      {pf.reconciler_override ? pf.reconciler_tier : pf.quality_tier}
                                    </span>
                                    {pf.reconciler_override && (
                                      <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 6, background: '#e11d4820', color: '#e11d48' }}>OVERRIDE</span>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: T.textDark, background: `${T.navy}08`, padding: '2px 10px', borderRadius: 10 }}>
                                    {totalCount}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px', color: T.textLight, fontSize: 11, whiteSpace: 'nowrap' }}>
                                  {formatTimestamp(pf.analyzed_at)}
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td colSpan={isQrs ? 6 : 7} style={{ padding: 0, background: T.offWhite }}>
                                    <div style={{ padding: '16px 24px' }}>
                                      {pf.summary && (
                                        <div style={{
                                          padding: '12px 16px', borderRadius: T.radiusSm,
                                          background: `${T.navy}06`, border: `1px solid ${T.navy}15`,
                                          marginBottom: 16, fontSize: 13, color: T.textDark, lineHeight: 1.6,
                                        }}>
                                          <span style={{ fontWeight: 700, color: T.navy }}>Summary: </span>
                                          {pf.summary}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {displayFindings.map((finding, fIdx) => {
                                          const fType = finding.type || '';
                                          if (fType === 'confidence_override') {
                                            return <ConfidenceOverrideCard key={fIdx} finding={finding} T={T} productQualityTier={pf.quality_tier} productReconcilerTier={pf.reconciler_tier} />;
                                          }
                                          if (fType === 'reconciler_conflict') {
                                            return <ReconcilerConflictCard key={fIdx} finding={finding} T={T} onResolve={handleResolveOverride} productQualityTier={pf.quality_tier} productReconcilerTier={pf.reconciler_tier} />;
                                          }
                                          return <DefaultFindingCard key={fIdx} finding={finding} T={T} />;
                                        })}
                                      </div>
                                      {displayFindings.length === 0 && (
                                        <div style={{ padding: 16, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
                                          No findings in this category for this product.
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalFindings > PAGE_SIZE && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center' }}>
                  <button
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
                      cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                      opacity: currentPage <= 1 ? 0.5 : 1,
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 12, color: T.textLight }}>
                    Page {currentPage} of {Math.ceil(totalFindings / PAGE_SIZE)}
                  </span>
                  <button
                    disabled={currentPage >= Math.ceil(totalFindings / PAGE_SIZE)}
                    onClick={() => setCurrentPage(p => p + 1)}
                    style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
                      cursor: currentPage >= Math.ceil(totalFindings / PAGE_SIZE) ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      opacity: currentPage >= Math.ceil(totalFindings / PAGE_SIZE) ? 0.5 : 1,
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Summary Tiles */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
                <div style={{
                  background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
                  boxShadow: T.shadow, padding: 20, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    High Scoring Products
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: '#22C55E' }}>
                    {historyData?.summary?.high_tier_products ?? highScoringCount}
                  </div>
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>
                    Products with quality_tier = HIGH
                  </div>
                </div>
                <div style={{
                  background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
                  boxShadow: T.shadow, padding: 20, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    Findings Resolved
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: T.navy }}>
                    {historyData?.summary?.findings_resolved ?? '--'}
                  </div>
                </div>
                <div style={{
                  background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
                  boxShadow: T.shadow, padding: 20, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    Override Resolution Rate
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: T.gold }}>
                    {historyData?.summary?.override_resolution_rate != null
                      ? `${historyData.summary.override_resolution_rate}%`
                      : '--'}
                  </div>
                </div>
              </div>

              {/* Time range toggle */}
              <div style={{ display: 'flex', gap: 4 }}>
                {([
                  { key: '10' as const, label: 'Last 10 Cycles' },
                  { key: '30' as const, label: 'Last 30 Days' },
                  { key: 'all' as const, label: 'All Time' },
                ]).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setHistoryRange(opt.key)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: `1px solid ${historyRange === opt.key ? T.navy : T.border}`,
                      background: historyRange === opt.key ? `${T.navy}12` : 'transparent',
                      color: historyRange === opt.key ? T.navy : T.textMid,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* History Table */}
              <div style={{
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
              }}>
                {(!historyData || !historyData.entries || historyData.entries.length === 0) ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
                      No History Data Yet
                    </div>
                    <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
                      Improvement history is recorded after each scoring cycle completes.
                    </div>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                          {['Cycle', 'Date', 'High Scoring Products', 'Findings Resolved', 'Resolution Rate'].map(col => (
                            <th key={col} style={{
                              padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                              color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                            }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.entries.map((entry, idx) => (
                          <tr key={idx} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                            <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy }}>{entry.cycle_id.slice(0, 8)}</td>
                            <td style={{ padding: '12px 16px', color: T.textDark }}>{formatDateShort(entry.date)}</td>
                            <td style={{ padding: '12px 16px' }}><span style={{ fontWeight: 700, color: '#22C55E' }}>{entry.high_tier_products}</span></td>
                            <td style={{ padding: '12px 16px' }}><span style={{ fontWeight: 700, color: T.navy }}>{entry.findings_resolved}</span></td>
                            <td style={{ padding: '12px 16px' }}><span style={{ fontWeight: 700, color: T.gold }}>{entry.override_resolution_rate}%</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PRODUCTS VIEW */}
      {pageView === 'products' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Search + Tier Filters */}
          <div style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row',
            gap: 12, alignItems: isMobile ? 'stretch' : 'center',
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={T.textLight} strokeWidth="2"
                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, NDC, manufacturer, or country..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px 10px 38px', borderRadius: 10,
                  border: `1px solid ${T.border}`, fontSize: 13, fontFamily: 'inherit',
                  background: T.white, color: T.textDark, boxSizing: 'border-box' as const,
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['all', 'HIGH', 'MODERATE', 'LOW'].map(tier => (
                <button
                  key={tier}
                  onClick={() => setTierFilter(tier)}
                  style={{
                    padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    border: `1px solid ${tierFilter === tier ? (tier === 'all' ? T.navy : tierColor(tier)) : T.border}`,
                    background: tierFilter === tier ? (tier === 'all' ? `${T.navy}12` : `${tierColor(tier)}12`) : T.white,
                    color: tierFilter === tier ? (tier === 'all' ? T.navy : tierColor(tier)) : T.textMid,
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                >
                  {tier === 'all' ? 'All Tiers' : tier}
                </button>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 13, color: T.textLight }}>
            Showing {productsForGrid.length} product{productsForGrid.length !== 1 ? 's' : ''}
            {(searchQuery || tierFilter !== 'all') && ` (filtered from ${findings.length} total)`}
          </div>

          {/* Card Grid */}
          {productsForGrid.length === 0 ? (
            <div style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
              padding: 40, textAlign: 'center',
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
                No Products Found
              </div>
              <div style={{ fontSize: 13, color: T.textLight }}>
                {searchQuery ? 'Try adjusting your search criteria.' : 'No products have been analyzed yet.'}
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: 16,
            }}>
              {productsForGrid.map(pf => (
                <div
                  key={pf.ndc}
                  onClick={() => setSelectedProductNdc(pf.ndc)}
                  style={{
                    background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
                    boxShadow: T.shadow, padding: 20, cursor: 'pointer', transition: 'all 0.2s',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = T.gold;
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(197,164,78,0.2)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = T.border;
                    (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadow;
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <ScoreRing score={pf.coo_score} size={56} strokeWidth={5} />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                        padding: '2px 8px', borderRadius: 10,
                        background: `${tierColor(pf.quality_tier)}18`,
                        color: tierColor(pf.quality_tier),
                      }}>
                        {pf.quality_tier || '--'}
                      </span>
                      {pf.reconciler_override && (
                        <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 6, background: '#e11d4820', color: '#e11d48' }}>OVERRIDE</span>
                      )}
                      {pf.reconciler_conflict && (
                        <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 6, background: '#d9770620', color: '#d97706' }}>CONFLICT</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark, marginBottom: 2, lineHeight: 1.3 }}>
                      {pf.product_name || pf.ndc}
                    </div>
                    <div style={{ fontSize: 11, color: T.textLight }}>NDC: {pf.ndc}</div>
                    {pf.manufacturer && <div style={{ fontSize: 11, color: T.textLight }}>{pf.manufacturer}</div>}
                  </div>

                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderTop: `1px solid ${T.borderLight}`, paddingTop: 10,
                  }}>
                    {!isQrs && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.textMid }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                        </svg>
                        {pf.coo_country || '--'}
                      </div>
                    )}
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: T.textDark,
                      background: `${T.navy}08`, padding: '2px 8px', borderRadius: 8,
                    }}>
                      {pf.findings.length} finding{pf.findings.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Finding Card Sub-Components
// ============================================================

interface FindingCardProps {
  finding: Finding;
  T: ReturnType<typeof useTheme>['T'];
  onResolve?: (findingId: number, action: string) => void;
  productQualityTier?: string;
  productReconcilerTier?: string;
}

function ConfidenceOverrideCard({ finding, T, productQualityTier, productReconcilerTier }: FindingCardProps) {
  const roseColor = '#e11d48';
  const scoringTier = finding.scoring_agent_tier || productQualityTier || 'UNKNOWN';
  const scoringScore = finding.scoring_agent_score;
  const reconcilerTier = productReconcilerTier || finding.reconciler_tier || 'UNKNOWN';

  return (
    <div style={{
      background: T.white, border: `1px solid ${roseColor}30`,
      borderRadius: T.radiusSm, padding: '16px 20px',
      borderLeft: `4px solid ${roseColor}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 8,
          background: `${roseColor}15`, color: roseColor,
          border: `1px solid ${roseColor}30`,
        }}>
          Confidence Override
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark, marginBottom: 12 }}>
        Reconciler assessed {reconcilerTier} &mdash; scoring agent tier is {scoringTier}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px', borderRadius: T.radiusSm,
        background: `${roseColor}06`, border: `1px solid ${roseColor}15`,
        marginBottom: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: T.textLight, fontWeight: 600 }}>Scoring Agent</span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
            background: `${tierColor(scoringTier)}18`, color: tierColor(scoringTier),
          }}>
            {scoringTier}
          </span>
          {scoringScore != null && (
            <span style={{ fontSize: 11, color: T.textMid }}>({scoringScore}%)</span>
          )}
        </div>
        <svg width="24" height="14" viewBox="0 0 24 14" fill="none">
          <path d="M0 7h20m0 0l-4-4m4 4l-4 4" stroke={roseColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: roseColor, fontWeight: 600 }}>Reconciler Override</span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
            background: `${tierColor(reconcilerTier)}18`, color: tierColor(reconcilerTier),
          }}>
            {reconcilerTier}
          </span>
        </div>
      </div>
      {finding.reconciler_reason && (
        <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6, marginBottom: 8 }}>
          {finding.reconciler_reason}
        </div>
      )}
      <div style={{ fontSize: 11, color: T.textLight, fontStyle: 'italic' }}>
        Override active until next scoring cycle
      </div>
    </div>
  );
}

function ReconcilerConflictCard({ finding, T, onResolve, productQualityTier, productReconcilerTier }: FindingCardProps) {
  const amberColor = '#d97706';
  const greenColor = '#059669';
  const scoringCoo = finding.scoring_agent_coo || 'Unknown';
  const scoringTier = finding.scoring_agent_tier || productQualityTier || 'UNKNOWN';
  const reconcilerCoo = finding.reconciler_coo || 'Unknown';
  const reconcilerTier = productReconcilerTier || finding.reconciler_tier || 'UNKNOWN';
  const llmCoo = finding.llm_coo || 'Unknown';
  const isResolved = finding.resolved || false;

  return (
    <div style={{
      background: T.white, border: `1px solid ${isResolved ? `${greenColor}30` : `${amberColor}30`}`,
      borderRadius: T.radiusSm, padding: '16px 20px',
      borderLeft: `4px solid ${isResolved ? greenColor : amberColor}`,
      opacity: isResolved ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 8,
          background: `${amberColor}15`, color: amberColor,
          border: `1px solid ${amberColor}30`,
        }}>
          Reconciler Conflict
        </span>
        {isResolved && (
          <span style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 8,
            background: `${greenColor}15`, color: greenColor,
            border: `1px solid ${greenColor}30`,
          }}>
            Resolved
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark, marginBottom: 12 }}>
        LLM believes COO should be {llmCoo}, not {reconcilerCoo}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '12px 16px', borderRadius: T.radiusSm,
        background: `${amberColor}06`, border: `1px solid ${amberColor}15`,
        marginBottom: 10,
      }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          padding: '8px 12px', borderRadius: 8, background: T.white, border: `1px solid ${T.border}`,
        }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: T.textLight, textTransform: 'uppercase' }}>Scoring Agent</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>{scoringCoo}</span>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
            background: `${tierColor(scoringTier)}18`, color: tierColor(scoringTier),
          }}>
            {scoringTier}
          </span>
        </div>
        <svg width="20" height="14" viewBox="0 0 20 14" fill="none" style={{ flexShrink: 0 }}>
          <path d="M0 7h16m0 0l-3-3m3 3l-3 3" stroke={T.textLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          padding: '8px 12px', borderRadius: 8, background: T.white, border: `1px solid ${T.border}`,
        }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: T.textLight, textTransform: 'uppercase' }}>Reconciler</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>{reconcilerCoo}</span>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
            background: `${tierColor(reconcilerTier)}18`, color: tierColor(reconcilerTier),
          }}>
            {reconcilerTier}
          </span>
        </div>
        <svg width="20" height="14" viewBox="0 0 20 14" fill="none" style={{ flexShrink: 0 }}>
          <path d="M0 7h16m0 0l-3-3m3 3l-3 3" stroke={amberColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          padding: '8px 12px', borderRadius: 8,
          background: `${amberColor}08`, border: `1px solid ${amberColor}30`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={amberColor} strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span style={{ fontSize: 9, fontWeight: 700, color: amberColor, textTransform: 'uppercase' }}>LLM Assessment</span>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: amberColor }}>{llmCoo}</span>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
            background: `${amberColor}18`, color: amberColor,
          }}>
            DISPUTES COO
          </span>
        </div>
      </div>
      {finding.llm_reason && (
        <div style={{
          fontSize: 12, color: T.textMid, lineHeight: 1.6, marginBottom: 8,
          padding: '8px 12px', borderRadius: T.radiusSm,
          background: `${amberColor}06`, borderLeft: `3px solid ${amberColor}40`,
          fontStyle: 'italic',
        }}>
          &ldquo;{finding.llm_reason}&rdquo;
        </div>
      )}
      {!isResolved && onResolve && finding.finding_id != null ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginTop: 12,
          paddingTop: 12, borderTop: `1px solid ${T.border}`,
        }}>
          <button
            onClick={() => onResolve(finding.finding_id!, 'accept_llm')}
            style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              border: `1px solid ${amberColor}`, background: amberColor, color: '#fff',
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
            }}
            title={`Override COO to "${llmCoo}" as suggested by LLM`}
          >
            Accept LLM COO ({llmCoo})
          </button>
          <button
            onClick={() => onResolve(finding.finding_id!, 'keep_reconciler')}
            style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              border: `1px solid ${T.border}`, background: T.white, color: T.textMid,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
            }}
            title={`Dismiss conflict, keep reconciler COO "${reconcilerCoo}"`}
          >
            Keep Reconciler ({reconcilerCoo})
          </button>
        </div>
      ) : isResolved ? (
        <div style={{
          fontSize: 11, fontWeight: 700, color: greenColor,
          display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={greenColor} strokeWidth="2">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Resolved
        </div>
      ) : (
        <div style={{
          fontSize: 11, fontWeight: 700, color: amberColor,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={amberColor} strokeWidth="2">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          Action Required: Human review recommended
        </div>
      )}
    </div>
  );
}

function DefaultFindingCard({ finding, T }: FindingCardProps) {
  const fType = finding.type || '';
  const fColor = findingTypeColor(fType);

  return (
    <div style={{
      background: T.white, border: `1px solid ${T.borderLight}`,
      borderRadius: T.radiusSm, padding: '12px 16px',
      borderLeft: `3px solid ${fColor}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%', background: fColor,
          flexShrink: 0, marginTop: 5,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
              padding: '1px 6px', borderRadius: 8,
              background: `${fColor}15`, color: fColor,
            }}>
              {findingTypeLabel(fType)}
            </span>
            {finding.ai_generated && (
              <span style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                padding: '1px 6px', borderRadius: 8,
                background: '#8B5CF615', color: '#8B5CF6',
              }}>
                AI Generated
              </span>
            )}
            {finding.priority && (
              <span style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                padding: '1px 6px', borderRadius: 8,
                background: finding.priority === 'high' ? '#EF444415' : finding.priority === 'medium' ? '#F59E0B15' : '#22C55E15',
                color: finding.priority === 'high' ? '#EF4444' : finding.priority === 'medium' ? '#F59E0B' : '#22C55E',
              }}>
                {finding.priority}
              </span>
            )}
          </div>
          {finding.factor && (
            <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark, marginBottom: 2 }}>
              {finding.factor}
              {finding.source && (
                <span style={{ fontWeight: 400, color: T.textLight, fontSize: 11 }}>
                  {' '}({finding.source})
                </span>
              )}
            </div>
          )}
          {finding.suggestion && (
            <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6, marginBottom: 4 }}>
              {String(finding.suggestion)}
            </div>
          )}
          {finding.result && (
            <div style={{ fontSize: 11, color: T.textLight, lineHeight: 1.4, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>Evidence: </span>
              {String(finding.result)}
            </div>
          )}
          {finding.sources_affected && (finding.sources_affected as string[]).length > 0 && (
            <div style={{ fontSize: 11, color: T.textLight, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>Sources: </span>
              {(finding.sources_affected as string[]).join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
